import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../../../../components';
import { cn } from '../../../../utils';

export interface ScrollSelectOption {
  text: string | ReactNode;
  value: number;
  className?: string;
}

export interface ScrollSelectProps {
  value: number;
  onChange: (value: string) => void;
  options: ScrollSelectOption[];
}

const SINGLE_ELEMENT_SIZE = 40;
const DEBOUNCE_TIME = 300;

export const ScrollSelect = ({
  value,
  onChange,
  options,
}: ScrollSelectProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const handleClickAdjustPosition = useCallback((index: number) => {
    containerRef.current?.scrollTo({
      top: index * SINGLE_ELEMENT_SIZE,
      behavior: 'smooth',
    });

    setSelectedIndex(index);
  }, []);

  const containerRef = useRef<HTMLDivElement>(null);
  const debouncedRef = useRef<NodeJS.Timeout>(null);

  const getNewValue = useCallback(
    (index: number) => {
      const newValue = options?.at(index)?.value;

      if (typeof newValue === 'number') onChange(newValue.toString());
    },
    [onChange, options]
  );

  useEffect(() => {
    const controller = new AbortController();

    containerRef.current?.addEventListener(
      'scroll',
      () => {
        if (debouncedRef.current) clearTimeout(debouncedRef.current);

        debouncedRef.current = setTimeout(() => {
          const scrollTop = containerRef.current?.scrollTop || 0;

          const newIndex = Math.floor(scrollTop / SINGLE_ELEMENT_SIZE);

          getNewValue(newIndex);
        }, DEBOUNCE_TIME);
      },
      controller
    );

    return () => {
      controller.abort();
      if (debouncedRef.current) clearTimeout(debouncedRef.current);
    };
  }, [getNewValue]);

  useEffect(() => {
    const foundIndex = options.findIndex(
      ({ value: optionValue }) => optionValue === value
    );

    if (foundIndex !== -1) handleClickAdjustPosition(foundIndex);
  }, [value, options, handleClickAdjustPosition]);

  return (
    <div className="relative flex h-full w-full flex-col items-center justify-center">
      <Button
        onClick={() => handleClickAdjustPosition(selectedIndex - 1)}
        disabled={selectedIndex <= 0}
        variant="ghost"
        size="custom"
        className={cn('size-8')}
        style={{
          height: SINGLE_ELEMENT_SIZE,
        }}
      >
        <ChevronUp className="size-6" />
      </Button>

      <div
        style={{
          height: SINGLE_ELEMENT_SIZE,
          top: SINGLE_ELEMENT_SIZE * 2,
        }}
        className="absolute mx-auto w-[80%] border-y"
      />

      <div
        ref={containerRef}
        style={{ maxHeight: SINGLE_ELEMENT_SIZE * 3 }}
        className="no-scrollbar relative flex w-full snap-y flex-col overflow-auto p-1"
      >
        <div
          style={{ height: SINGLE_ELEMENT_SIZE }}
          className="w-full shrink-0"
        />

        {options.map(({ value: optionValue, text }, idx) => (
          <Button
            key={optionValue}
            size="custom"
            variant={'ghost'}
            style={{ height: SINGLE_ELEMENT_SIZE }}
            className={cn(
              'text-muted-foreground w-full shrink-0 snap-center text-base font-semibold',
              {
                'text-primary': selectedIndex === idx,
              }
            )}
            onClick={() => handleClickAdjustPosition(idx)}
          >
            {text}
          </Button>
        ))}
        <div
          style={{ height: SINGLE_ELEMENT_SIZE }}
          className="w-full shrink-0"
        />
      </div>

      <Button
        style={{
          height: SINGLE_ELEMENT_SIZE,
        }}
        onClick={() => handleClickAdjustPosition(selectedIndex + 1)}
        variant="ghost"
        size="custom"
        disabled={selectedIndex >= options.length}
      >
        <ChevronDown className="size-6" />
      </Button>
    </div>
  );
};
